!function () {
    "use strict";

    const SELECTOR_ROWS_HOLDER = "[data-timeline-rows]";
    const SELECTOR_ROW = "[data-timeline-row]";
    const SELECTOR_ROW_EDITOR = "[data-timeline-row-editor]";
    const SELECTOR_BUTTON = "[data-timeline-button]";
    const SELECTOR_BLOCK_SCROLL = "[data-block-scroll]";
    const CLASS_SELECTED = "is-selected";

    customElements.get("timeline-component") || customElements.define("timeline-component", class extends HTMLElement {
        constructor() {
            super();

            this.rows = this.querySelectorAll(SELECTOR_ROW);
            if (this.rows.length < 2) return;

            this.rowsWidth = 0;
            this.rowsHeight = [];
            this.holderTop = this.getBoundingClientRect().top + window.scrollY;
            this.holderHeight = this.offsetHeight;
            this.buttons = this.querySelectorAll(SELECTOR_BUTTON);
            this.rowsHolder = this.querySelector(SELECTOR_ROWS_HOLDER);
            this.requestAnimation = null;
            this.isDesktopView = !window.theme.isMobile();
            this.isScrollEnabled = false;
            this.activeImageIndex = 0;
            this.scrollEvent = (event) => this.scrollEvents(event);
            this.resizeEvent = () => this.resizeEvents();
        }

        connectedCallback() {
            this.calculateRowsDimensions();
            this.requestAnimation = requestAnimationFrame(() => this.calculatePosition());

            if (this.isDesktopView && !this.isScrollEnabled) {
                this.isScrollEnabled = true;
                document.addEventListener("theme:scroll", this.scrollEvent);
            }

            this.rowsHolder.addEventListener("scroll", this.scrollEvent);
            document.addEventListener("theme:resize:width", this.resizeEvent);

            if (this.buttons.length) {
                this.buttons.forEach((button, buttonIndex) => {
                    button.addEventListener("click", (clickEvent) => {
                        clickEvent.preventDefault();
                        const row = clickEvent.currentTarget.closest(SELECTOR_ROW);

                        if (!row) return;

                        if (window.theme.isMobile()) {
                            const padding = 16;
                            const scrollLeft = row.offsetLeft - padding;
                            this.rowsHolder.scrollTo({ left: scrollLeft, behavior: "smooth" });
                        } else {
                            const maxRowHeight = Math.max(...this.rowsHeight);
                            const segmentHeight = this.holderHeight / this.rows.length;
                            const holderBoundsTop = this.getBoundingClientRect().top;
                            const scrollTarget = (buttonIndex > 0 && maxRowHeight < segmentHeight ? segmentHeight * buttonIndex + holderBoundsTop : holderBoundsTop) + 1;
                            window.theme.scrollTo(scrollTarget);
                        }
                    });
                });
            }
        }

        resizeEvents() {
            this.holderTop = this.getBoundingClientRect().top + window.scrollY;
            this.holderHeight = this.offsetHeight;
            this.calculateRowsDimensions();
            this.requestAnimation = requestAnimationFrame(() => this.calculatePosition());

            const isDesktop = !window.theme.isMobile();

            if (isDesktop && !this.isScrollEnabled) {
                this.isScrollEnabled = true;
                document.addEventListener("theme:scroll", this.scrollEvent);
            } else if (!isDesktop && this.isScrollEnabled) {
                this.isScrollEnabled = false;
                document.removeEventListener("theme:scroll", this.scrollEvent);
            }
        }

        scrollEvents(event) {
            if (!this.requestAnimation) {
                const scrollTarget = event.currentTarget;
                this.requestAnimation = requestAnimationFrame(() => this.calculatePosition(scrollTarget));
            }
        }

        removeAnimationFrame() {
            if (this.requestAnimation) {
                cancelAnimationFrame(this.requestAnimation);
                this.requestAnimation = null;
            }
        }

        calculateRowsDimensions() {
            if (!this.rows.length) return;

            this.rowsHeight = [];
            this.rowsWidth = 0;
            let cumulativeHeight = 0;

            this.rows.forEach((row, rowIndex) => {
                this.rowsHeight.push(row.offsetHeight);
                this.rowsWidth += row.offsetWidth + parseInt(getComputedStyle(row).marginRight);

                if (window.Shopify.designMode) {
                    const editorRows = this.querySelectorAll(SELECTOR_ROW_EDITOR);
                    if (editorRows.length) {
                        cumulativeHeight += row.offsetHeight;
                        editorRows[rowIndex]?.style.setProperty("--row-height-min", `${row.offsetHeight}px`);
                        editorRows[rowIndex + 1]?.style.setProperty("--row-top-mobile", `${cumulativeHeight}px`);
                    }
                }
            });
        }

        calculatePosition(scrollContainer = null) {
            this.removeAnimationFrame();
            this.holderTop = this.getBoundingClientRect().top + window.scrollY;

            const isDesktop = !window.theme.isMobile();
            const segmentHeight = this.holderHeight / this.rows.length;
            let currentTop = this.holderTop;
            const viewportHeight = window.innerHeight;
            const scrollThreshold = window.scrollY + 1.5 * viewportHeight;
            let mobilePercent = 0;

            if (!isDesktop && scrollContainer) {
                const scrollLeft = scrollContainer.scrollLeft - 16;
                const containerWidth = scrollContainer.offsetWidth;
                mobilePercent = scrollLeft / (this.rowsWidth - containerWidth) * 100;
                this.style.setProperty("--percent-mobile", `${mobilePercent}%`);
            }

            if (isDesktop && (scrollThreshold < this.holderTop || scrollThreshold > this.holderTop + this.holderHeight + viewportHeight)) return;

            this.rows.forEach((row, rowIndex) => {
                if (rowIndex > 0) {
                    const rowOffsetPercent = row.offsetLeft / this.rowsWidth * 100;
                    let isActive = mobilePercent > rowOffsetPercent;

                    if (isDesktop) {
                        isActive = currentTop + segmentHeight < scrollThreshold;

                        if (currentTop - segmentHeight < scrollThreshold) {
                            row.previousElementSibling?.style.setProperty("--percent-desktop", (scrollThreshold - currentTop) / segmentHeight * 100 + "%");
                        }
                    }

                    row.classList.toggle(CLASS_SELECTED, isActive);
                }

                currentTop += segmentHeight;
            });

            if (isDesktop) {
                const activeIndex = Math.max(0, [...this.rows].findLastIndex(r => r.classList.contains(CLASS_SELECTED)));
                const targetRow = this.rows[activeIndex];
                this.rowsHolder.scrollTo({ top: targetRow.offsetTop, behavior: "smooth" });

                if (activeIndex !== this.activeImageIndex) {
                    const prevIndex = this.activeImageIndex;
                    this.activeImageIndex = activeIndex;

                    this.rows.forEach((row, i) => {
                        const figure = row.querySelector(".timeline__image-figure");
                        if (!figure) return;

                        if (i === activeIndex || i === prevIndex) {
                            figure.style.transition = "transform 0.5s";
                        } else {
                            figure.style.transition = "none";
                        }

                        figure.style.transform = i <= activeIndex ? "translateY(0)" : "translateY(100%)";
                    });
                }
            }
        }

        disconnectedCallback() {
            if (this.isScrollEnabled) {
                this.isScrollEnabled = false;
                document.removeEventListener("theme:scroll", this.scrollEvent);
            }

            this.rowsHolder.removeEventListener("scroll", this.scrollEvent);
            document.removeEventListener("theme:resize:width", this.resizeEvent);
        }

        onBlockSelect(event) {
            const scrollContainer = this.querySelector(SELECTOR_BLOCK_SCROLL);
            if (!scrollContainer) return;

            const block = event.srcElement;
            const blockIndex = [...block.parentElement.children].indexOf(block);
            const targetRow = this.rows[blockIndex];

            if (targetRow) {
                scrollContainer.scrollTo({ top: 0, left: targetRow.offsetLeft, behavior: "smooth" });
            }
        }
    });
}();
